import type { ApiResponse, MerchantActionReceipt, Proposal, StoredProposal } from '../../services/api'

/**
 * What became of a Confirm or Cancel press on a proposal Ask prepared (MAR-175).
 *
 * Pure, so the copy is pinned in one place and the hook and the card cannot
 * disagree about what a status means.
 *
 * - `applied`   — the change happened; the receipt is the server's.
 * - `cancelled` — withdrawn; nothing changed.
 * - `refused`   — final: the token is spent. `changedNothing` says whether the
 *                 card may add "Nothing was changed." — true of an ordinary refusal,
 *                 FALSE when the change is still going through (`proposal_applying`)
 *                 or failed part-way (`proposal_failed`, a 500 write failure). It is
 *                 decided HERE, beside the status, so the card cannot claim nothing
 *                 changed about an answer that never said so.
 * - `retry`     — we cannot tell what happened (no response, a gateway 502/504, or
 *                 a 5xx carrying no `details.reason`), or the server SAID the refusal
 *                 is resumable (`details.retryable: true`, e.g. `500 question_unreadable`
 *                 with the receipt left `applying`). The token is kept, and pressing
 *                 again is safe because a replayed confirm answers the original
 *                 receipt rather than writing twice.
 */
export type ProposalOutcome =
  | { kind: 'applied'; receipt: MerchantActionReceipt; replayed: boolean }
  | { kind: 'cancelled' }
  | { kind: 'refused'; copy: string; changedNothing: boolean }
  | { kind: 'retry'; copy: string }

export type ProposalAction = 'confirm' | 'cancel'

export const PROPOSAL_COPY = {
  notAdmin: 'Only an owner or admin can confirm this.',
  notYours: 'Only the person who asked for this can act on it.',
  expired: 'This proposal expired. Ask again.',
  superseded: 'Something changed since she prepared this. Ask again for a fresh one.',
  // The server's words for the same fact (governance-api `question_erased`), so the inbox and
  // the card say one thing. It already says nothing was sent: no "Nothing was changed." after it.
  erased: 'This customer asked to be forgotten — nothing was sent.',
  erasedMidApply: 'This customer asked to be forgotten while the answer was going through — check the Ledger before asking again.',
  writesOff: 'Changes from Ask are switched off.',
  notFound: 'I can’t find this proposal any more. Ask again.',
  alreadyCancelled: 'This was already cancelled.',
  notAsPrepared: 'This can’t go ahead as prepared. Ask again for a fresh one.',
  failed: 'That didn’t complete. Check the Ledger before asking again.',
  applying: 'This is already going through — check the Ledger in a moment.',
  noAnswer: 'The store didn’t give a clear answer, so I can’t tell whether that went through. Try again — repeating it is safe.',
  unreachable: 'I couldn’t reach the store, so I can’t tell whether that went through. Try again — repeating it is safe.',
  cancelled: 'Cancelled — nothing changed.',
  nothingChanged: 'Nothing was changed.',
  inert: 'Prepared earlier — ask again to change it.',
} as const

function detailsOf(
  res: ApiResponse | null | undefined
): { reason?: string; cause?: string; retryable?: boolean; receipt?: MerchantActionReceipt } {
  const d = res?.details
  if (!d || typeof d !== 'object' || Array.isArray(d)) return {}
  const { reason, cause, retryable, receipt } = d as {
    reason?: unknown
    cause?: unknown
    retryable?: unknown
    receipt?: unknown
  }
  return {
    ...(retryable === true ? { retryable: true } : {}),
    ...(typeof reason === 'string' ? { reason } : {}),
    ...(typeof cause === 'string' ? { cause } : {}),
    ...(receipt && typeof receipt === 'object' ? { receipt: receipt as MerchantActionReceipt } : {}),
  }
}

/**
 * Map a confirm/cancel response onto what the merchant is told. Branches on the
 * HTTP status and `details.reason` — never on `error`, which is prose.
 */
export function proposalOutcome(
  action: ProposalAction,
  res: ApiResponse<{ receipt: MerchantActionReceipt; replayed?: boolean }> | null | undefined
): ProposalOutcome {
  if (res?.success && res.data?.receipt) {
    if (action === 'cancel') return { kind: 'cancelled' }
    return { kind: 'applied', receipt: res.data.receipt, replayed: res.data.replayed === true }
  }
  // No status: the request never got an answer. We cannot claim nothing changed.
  if (!res || typeof res.status !== 'number') return { kind: 'retry', copy: PROPOSAL_COPY.unreachable }

  const { reason, cause, retryable, receipt } = detailsOf(res)
  const refused = (copy: string, changedNothing = true): ProposalOutcome => ({ kind: 'refused', copy, changedNothing })
  const retry: ProposalOutcome = { kind: 'retry', copy: PROPOSAL_COPY.noAnswer }

  // ⚠️ A 5xx is not a refusal unless the server SAID what it refused. A gateway
  // 502/504 or a bare 5xx means the request may or may not have landed: keep the
  // token, because a replay answers the original receipt rather than writing twice.
  // The server said pressing again will finish it (the receipt is mid-apply): keep the token.
  if (retryable === true) return retry
  if (res.status === 502 || res.status === 504) return retry
  if (res.status >= 500 && !reason) return retry

  switch (res.status) {
    case 403:
      return refused(reason === 'proposal_actor_mismatch' ? PROPOSAL_COPY.notYours : PROPOSAL_COPY.notAdmin)
    case 404:
      return refused(PROPOSAL_COPY.notFound)
    case 410:
      return refused(PROPOSAL_COPY.expired)
    case 503:
      // Only the kill switch is a refusal. Any other 503 is the store being unwell.
      return reason === 'writes_disabled' ? refused(PROPOSAL_COPY.writesOff) : retry
    case 409:
      // A cancel that lost to a confirm: the change DID happen, and saying
      // otherwise would be the one dishonest answer available here.
      if (reason === 'proposal_applied' && receipt?.status === 'applied') {
        return { kind: 'applied', receipt, replayed: true }
      }
      // Mid-write — for a confirm AND a cancel. Never "Nothing was changed".
      if (reason === 'proposal_applying') return refused(PROPOSAL_COPY.applying, false)
      if (reason === 'proposal_failed') return refused(PROPOSAL_COPY.failed, false)
      // Erasure reached the receipt (MAR-175). Three different facts, by the receipt it carries:
      //  - `applied`: the answer went out before the erasure — something DID change, so the
      //    receipt is shown, never "nothing was sent";
      //  - `applying`: we cannot say it was not sent — point at the Ledger;
      //  - otherwise: nothing was sent, and the copy says so itself.
      if (reason === 'proposal_superseded' && cause === 'customer_erased') {
        if (receipt?.status === 'applied') return { kind: 'applied', receipt, replayed: true }
        if (receipt?.status === 'applying') return refused(PROPOSAL_COPY.erasedMidApply, false)
        return refused(PROPOSAL_COPY.erased, false)
      }
      if (reason === 'proposal_superseded') return refused(PROPOSAL_COPY.superseded)
      if (reason === 'proposal_cancelled') return refused(PROPOSAL_COPY.alreadyCancelled)
      if (reason === 'proposal_expired') return refused(PROPOSAL_COPY.expired)
      return refused(PROPOSAL_COPY.notAsPrepared)
    default:
      // A 5xx that names its reason (a write that failed): it may have half-happened.
      if (res.status >= 500) return refused(PROPOSAL_COPY.failed, false)
      return refused(PROPOSAL_COPY.notAsPrepared)
  }
}

/** Is this past its expiry at `now`? An unreadable expiry counts as expired: fail closed. */
export function proposalExpired(expiresAt: string, now: number): boolean {
  const at = Date.parse(expiresAt)
  return Number.isNaN(at) || now >= at
}

/**
 * The transcript's copy of a proposal: every field but the token. Built by
 * listing what is KEPT, so a field added to the wire shape later cannot carry a
 * secret into the transcript by default.
 */
export function withoutToken(p: Proposal | StoredProposal): StoredProposal {
  return {
    receiptId: p.receiptId,
    capability: p.capability,
    summary: p.summary,
    ...(p.before !== undefined ? { before: p.before } : {}),
    ...(p.after !== undefined ? { after: p.after } : {}),
    // In memory only: the server sends `display` on the live response and never stores it.
    ...(p.display && typeof p.display.question === 'string' ? { display: { question: p.display.question } } : {}),
    expiresAt: p.expiresAt,
  }
}

/** A proposal the client can act on: a token and the fields it is shown by. */
export function isLiveProposal(p: unknown): p is Proposal {
  if (!p || typeof p !== 'object') return false
  const o = p as Record<string, unknown>
  return (
    typeof o.token === 'string' &&
    o.token.length > 0 &&
    typeof o.receiptId === 'string' &&
    typeof o.summary === 'string' &&
    typeof o.expiresAt === 'string' &&
    (o.capability === 'authority_change' || o.capability === 'customer_answer') &&
    !!o.after &&
    typeof o.after === 'object'
  )
}
